import React, { useContext, useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, FormikProps, Form, FormikHelpers } from 'formik'
import _ from 'lodash'
import * as yup from 'yup'
import OnFormikValidationError from 'components/OnFormikValidationError'
import {
    selectBlockedCampaignsUuids,
    setBlockedCampaigns,
    CAMPAIGN_STATUS_ENUM,
    REQUIREMENT_TYPE_ENUM,
} from 'state/campaigns'
import { BlockedCampaign } from 'services/api/campaigns'
import { convertToBackendStr, parseBackendDate, addDay } from 'services/dates'
import { showSnackBar } from 'services/toast'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import { BLOCKED_CAMPAIGNS_TYPE, FollowerRequirementData, Requirement } from '../CampaignRequirements'
import { SaveContainer } from './styles'
import Field from 'components/Shared/Fields'
import Editor from './Editor'
import Divider from 'components/Shared/Divider'
import { selectorEditorErrors, COMPLETE_SET_UP_ERRORS } from 'state/error'
import SaveAndSubmitButtonGroup from 'components/Campaign/Editor/SaveAndSubmitButtonGroup'
import { baseValidationSchema, locationsRequirementValidation } from './baseValidationSchema'
import { ERROR_MESSAGES } from './errorMessages'
import { runValidateSchema, validateFieldsAreAllPassed, setInitialTouched } from 'components/utils'
import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import EditCampaignRestricted from 'components/Shared/Permission/EditCampaignRestricted'
import { selectIsInternalUser } from 'state/profile'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { LocationData, LocationDataFormat } from '../CampaignRequirements/LocationRequirement'
import { generateUuid } from 'services/uuid'
import { usePermission } from 'hooks/permissions'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'

interface SaveFormProps {
    campaign: any
    isSaving: boolean
    handleSubmit: (data: any, isPublish: boolean) => void
}

const SaveForm: React.FC<SaveFormProps> = ({ campaign, isSaving, handleSubmit }) => {
    const [t] = useTranslation()
    const [currentValue, setValue] = useState<any>(null)
    const isPublish = useRef(true)
    const dispatch = useDispatch()
    const viewType = useContext(ViewTypeContext)
    const blockedCampaignsUuids = useSelector(selectBlockedCampaignsUuids)
    const errors = useSelector(selectorEditorErrors(Object.values(COMPLETE_SET_UP_ERRORS)))
    const isInternalUser = useSelector(selectIsInternalUser)
    const isAbleToReview =
        usePermission(CampaignStatusControl.ReviewCampaign) && campaign.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW
    const isAbleToLaunch =
        (campaign.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const handleSubmitForm = (data: any) => {
        handleSubmit(data, isPublish.current)
    }

    const validationSchema = useMemo(() => {
        const isCampaignDraftOrForEdit = [CAMPAIGN_STATUS_ENUM.DRAFT, CAMPAIGN_STATUS_ENUM.FOR_EDIT].includes(
            campaign?.status,
        )
        const schema = baseValidationSchema(isCampaignDraftOrForEdit)
        if (!isInternalUser) {
            return schema
        }
        const compareDate = campaign.time_end
            ? new Date(
                  parseBackendDate(campaign.time_end, campaign.country, {
                      showTime: true,
                      showYear: false,
                      addSeconds: -1,
                  }),
              )
            : null
        const payout_deadline = yup
            .date()
            .nullable()
            .when([], () => {
                if (compareDate) {
                    return isCampaignDraftOrForEdit
                        ? yup
                              .date()
                              .nullable()
                              .notRequired()
                              .min(compareDate, t('errorMessage.businessLogic.payoutBeforeEndDate'))
                        : yup
                              .date()
                              .nullable()
                              .required(t('errorMessage.validation.datePicker.selectDate'))
                              .min(compareDate, t('errorMessage.businessLogic.payoutBeforeEndDate'))
                } else {
                    return isCampaignDraftOrForEdit
                        ? yup.date().nullable().notRequired()
                        : yup.date().nullable().required(t('errorMessage.validation.datePicker.selectDate'))
                }
            })
        return schema.shape({
            payout_deadline,
        })
    }, [campaign, baseValidationSchema, isInternalUser])

    const initialValue = useMemo(() => {
        campaign.requirementsData = campaign.requirements ? _.clone(campaign.requirements) : []

        const getByRequirementType =
            (type: REQUIREMENT_TYPE_ENUM) =>
            (requirement: any): boolean =>
                requirement.type === type

        const getRequirementIndex = (type: REQUIREMENT_TYPE_ENUM) => {
            if (Array.isArray(campaign.requirementsData) && campaign.requirementsData.length > 0) {
                return campaign.requirementsData.findIndex(getByRequirementType(type))
            }
            return -1
        }

        const occupationsRequirementIdx = getRequirementIndex(REQUIREMENT_TYPE_ENUM.OCCUPATION)

        if (occupationsRequirementIdx > -1) {
            const occupationsRequirement = campaign.requirementsData[occupationsRequirementIdx]
            campaign.requirementsData.splice(occupationsRequirementIdx, 1, {
                ...occupationsRequirement,
                type: REQUIREMENT_TYPE_ENUM.OCCUPATION,
                properties: {
                    occupations: occupationsRequirement.properties.occupations.map((item: any) => item.value),
                },
            })
        }

        const profileTypesRequirementIdx = getRequirementIndex(REQUIREMENT_TYPE_ENUM.PROFILE_TYPE)

        if (profileTypesRequirementIdx > -1) {
            const profileTypesRequirement = campaign.requirementsData[profileTypesRequirementIdx]
            campaign.requirementsData.splice(profileTypesRequirementIdx, 1, {
                ...profileTypesRequirement,
                type: REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
                properties: {
                    profile_types: profileTypesRequirement.properties.profile_types.map((item: any) => item.value),
                },
            })
        }

        const followerRequirementIdx = getRequirementIndex(REQUIREMENT_TYPE_ENUM.FOLLOWER)

        if (followerRequirementIdx > -1) {
            let followerRequirements = []
            const followerRequirement = campaign.requirementsData[followerRequirementIdx]

            followerRequirements = followerRequirement.properties.followers.map((requirement: any) => ({
                type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                properties: {
                    followers: requirement,
                },
            }))
            campaign.requirementsData.splice(followerRequirementIdx, 1, ...followerRequirements)
        }

        const locationRequirement = campaign.requirementsData.find(getByRequirementType(REQUIREMENT_TYPE_ENUM.LOCATION))
        if (locationRequirement) {
            locationRequirement.properties.locations.forEach((location: LocationData) => {
                for (const key in location) {
                    if (location[key] === null) {
                        location[key] = { uuid: generateUuid(), id: null, name: null }
                    } else {
                        location[key] = location[key] as LocationDataFormat
                    }
                }
            })
        }
        if (campaign.blocked_campaigns && campaign.blocked_campaigns.length > 0) {
            dispatch(
                setBlockedCampaigns({
                    uuids: campaign.blocked_campaigns.map((campaign: BlockedCampaign) => campaign.uuid),
                }),
            )
            campaign.requirementsData.push({
                uuid: generateUuid(),
                type: BLOCKED_CAMPAIGNS_TYPE,
                properties: { inputValue: null, uuids: null },
            })
        }
        campaign.payout_deadline = campaign.payout_deadline
            ? parseBackendDate(campaign.payout_deadline, campaign.country, {
                  showTime: false,
                  showYear: true,
                  addSeconds: -1,
              })
            : null
        return campaign
    }, [campaign])

    const formErrors = useMemo(() => {
        if (!errors || !currentValue) return {}
        const returnErrors: any = _.cloneDeep(errors)
        const requirementsErrors: any = []
        if (returnErrors) {
            if (returnErrors[COMPLETE_SET_UP_ERRORS.DESCRIPTION]) {
                returnErrors[COMPLETE_SET_UP_ERRORS.DESCRIPTION] =
                    ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.DESCRIPTION][returnErrors[COMPLETE_SET_UP_ERRORS.DESCRIPTION]]
            }
            if (returnErrors[COMPLETE_SET_UP_ERRORS.IMAGES]) {
                returnErrors[COMPLETE_SET_UP_ERRORS.IMAGES] =
                    ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.IMAGES][returnErrors[COMPLETE_SET_UP_ERRORS.IMAGES]]
            }
            if (returnErrors[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE]) {
                returnErrors[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE] =
                    ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE][
                        returnErrors[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE]
                    ]
            }
        }
        if (currentValue && currentValue['requirementsData'] && Array.isArray(currentValue['requirementsData'])) {
            currentValue['requirementsData'].forEach((requirement: any) => {
                const returnErrorFormat = _.cloneDeep(requirement)
                const dataError =
                    errors && errors[COMPLETE_SET_UP_ERRORS.REQUIREMENTS]
                        ? errors[COMPLETE_SET_UP_ERRORS.REQUIREMENTS]
                        : null
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.AGE) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.AGE]
                    if (code) {
                        returnErrorFormat['properties']['min'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.AGE]['min'][code]
                        returnErrorFormat['properties']['max'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.AGE]['max'][code]
                    } else {
                        returnErrorFormat['properties']['min'] = ''
                        returnErrorFormat['properties']['max'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.GENDER) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.GENDER]
                    if (code) {
                        returnErrorFormat['properties']['value'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.GENDER][code]
                    } else {
                        returnErrorFormat['properties']['value'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.FOLLOWER) {
                    const platform =
                        requirement &&
                        requirement['properties'] &&
                        requirement['properties']['followers'] &&
                        requirement['properties']['followers']['platform']
                    const code =
                        dataError &&
                        dataError[REQUIREMENT_TYPE_ENUM.FOLLOWER] &&
                        dataError[REQUIREMENT_TYPE_ENUM.FOLLOWER][platform]
                    if (code) {
                        returnErrorFormat['properties']['followers'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.FOLLOWER][
                                platform
                            ][code]
                    } else {
                        returnErrorFormat['properties']['followers'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.OCCUPATION) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.OCCUPATION]
                    if (code) {
                        returnErrorFormat['properties']['occupations'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.OCCUPATION][code]
                    } else {
                        returnErrorFormat['properties']['occupations'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.LOCATION) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.LOCATION]
                    if (code) {
                        returnErrorFormat['properties']['locations'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.LOCATION][code]
                    } else {
                        returnErrorFormat['properties']['locations'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.PROFILE_TYPE) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.PROFILE_TYPE]
                    if (code) {
                        returnErrorFormat['properties']['profile_types'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.PROFILE_TYPE][
                                code
                            ]
                    } else {
                        returnErrorFormat['properties']['profile_types'] = ''
                    }
                }
                if (requirement['type'] === REQUIREMENT_TYPE_ENUM.OTHERS) {
                    const code = dataError && dataError[REQUIREMENT_TYPE_ENUM.OTHERS]
                    if (code) {
                        returnErrorFormat['properties']['value'] =
                            ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][REQUIREMENT_TYPE_ENUM.OTHERS][code]
                    } else {
                        returnErrorFormat['properties']['value'] = ''
                    }
                }
                if (returnErrorFormat['type'] !== 'BLOCKED_CAMPAIGNS') {
                    requirementsErrors.push(returnErrorFormat)
                }
            })
        }
        if (requirementsErrors.length > 0) {
            returnErrors['requirementsData'] = requirementsErrors
            delete returnErrors[COMPLETE_SET_UP_ERRORS.REQUIREMENTS]
        }
        return returnErrors
    }, [errors, currentValue])

    const initialTouched = useMemo(() => {
        const errors = _.cloneDeep(formErrors)
        return setInitialTouched(errors)
    }, [formErrors])

    const validate = useCallback(
        async values => {
            const schemaErrors = await runValidateSchema(values, validationSchema)
            if (formErrors && isPublish.current) {
                const returnError: any = _.cloneDeep(formErrors)
                const requirementsError = errors && errors['requirements']
                const newRequirementErrors: any = []
                if (
                    returnError[COMPLETE_SET_UP_ERRORS.IMAGES] &&
                    Array.isArray(values.images) &&
                    values.images.length > 0
                ) {
                    if (schemaErrors && schemaErrors[COMPLETE_SET_UP_ERRORS.IMAGES]) {
                        returnError[COMPLETE_SET_UP_ERRORS.IMAGES] = schemaErrors[COMPLETE_SET_UP_ERRORS.IMAGES]
                    } else {
                        returnError[COMPLETE_SET_UP_ERRORS.IMAGES] = ''
                    }
                }
                if (returnError[COMPLETE_SET_UP_ERRORS.DESCRIPTION] && values.description) {
                    if (schemaErrors && schemaErrors[COMPLETE_SET_UP_ERRORS.DESCRIPTION]) {
                        returnError[COMPLETE_SET_UP_ERRORS.DESCRIPTION] =
                            schemaErrors[COMPLETE_SET_UP_ERRORS.DESCRIPTION]
                    } else {
                        returnError[COMPLETE_SET_UP_ERRORS.DESCRIPTION] = ''
                    }
                }

                if (values.payout_deadline) {
                    if (schemaErrors && schemaErrors[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE]) {
                        returnError[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE] =
                            schemaErrors[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE]
                    } else {
                        returnError[COMPLETE_SET_UP_ERRORS.PAYOUT_DEADLINE] = ''
                    }
                }

                if (values['requirementsData'] && Array.isArray(values['requirementsData'])) {
                    values['requirementsData'].forEach(
                        (requirementError: { type: number | string; properties: any }, index: number) => {
                            const { type } = requirementError
                            const newError: { type?: number | string; properties: any } = _.cloneDeep(requirementError)
                            const error: any = requirementsError && requirementsError[type]
                            switch (type) {
                                case REQUIREMENT_TYPE_ENUM.AGE: {
                                    const ageRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.AGE,
                                    )
                                    if (!error && ageRequirement) {
                                        newError['properties']['min'] = ''
                                        newError['properties']['max'] = ''
                                    }
                                    if (error && ageRequirement && ageRequirement['properties']) {
                                        const isMinOrMaxHasValue = Boolean(
                                            ageRequirement['properties']['min'] || ageRequirement['properties']['max'],
                                        )

                                        newError['properties']['min'] = isMinOrMaxHasValue
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.AGE
                                              ]['min'][error]
                                        newError['properties']['max'] = isMinOrMaxHasValue
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.AGE
                                              ]['min'][error]
                                    }

                                    break
                                }

                                case REQUIREMENT_TYPE_ENUM.GENDER: {
                                    const genderRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.GENDER,
                                    )
                                    if (!error && genderRequirement) {
                                        newError['properties']['value'] = ''
                                    }
                                    if (error && genderRequirement) {
                                        const isGenderFilled = Boolean(genderRequirement['properties']['value'])
                                        newError['properties']['value'] = isGenderFilled
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.GENDER
                                              ][error]
                                    }
                                    break
                                }
                                case REQUIREMENT_TYPE_ENUM.FOLLOWER: {
                                    const followerValue =
                                        values['requirementsData'][index] &&
                                        values['requirementsData'][index]['properties']['followers']

                                    if (!error && followerValue) {
                                        newError['properties']['followers'] = ''
                                    }
                                    if (error && followerValue) {
                                        if (followerValue['min'] || followerValue['max']) {
                                            newError['properties']['followers'] = ''
                                        } else {
                                            newError['properties']['followers'] =
                                                ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                    REQUIREMENT_TYPE_ENUM.FOLLOWER
                                                ][followerValue['platform']][error[followerValue['platform']]]
                                        }
                                    }
                                    break
                                }
                                case REQUIREMENT_TYPE_ENUM.OCCUPATION: {
                                    const occupationRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.OCCUPATION,
                                    )
                                    if (!error && occupationRequirement) {
                                        newError['properties']['occupations'] = ''
                                    }
                                    if (
                                        error &&
                                        occupationRequirement &&
                                        occupationRequirement['properties'] &&
                                        occupationRequirement['properties']['occupations'] &&
                                        Array.isArray(occupationRequirement['properties']['occupations'])
                                    ) {
                                        const isSelectedOccupations =
                                            occupationRequirement['properties']['occupations'].length > 0

                                        newError['properties']['occupations'] = isSelectedOccupations
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.OCCUPATION
                                              ][error]
                                    }

                                    break
                                }
                                case REQUIREMENT_TYPE_ENUM.LOCATION: {
                                    const locationRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.LOCATION,
                                    )
                                    if (!error && locationRequirement) {
                                        newError['properties']['locations'] = ''
                                    }
                                    if (
                                        error &&
                                        locationRequirement &&
                                        locationRequirement['properties'] &&
                                        locationRequirement['properties']['locations'] &&
                                        Array.isArray(locationRequirement['properties']['locations']) &&
                                        locationRequirement['properties']['locations'].length > 0
                                    ) {
                                        const allFieldBeenFilled = locationsRequirementValidation(
                                            locationRequirement['properties']['locations'],
                                        )
                                        newError['properties']['locations'] = allFieldBeenFilled
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.LOCATION
                                              ][error]
                                    }

                                    break
                                }
                                case REQUIREMENT_TYPE_ENUM.PROFILE_TYPE: {
                                    const profileTypesRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
                                    )
                                    if (!error && profileTypesRequirement) {
                                        newError['properties']['profile_types'] = ''
                                    }
                                    if (
                                        error &&
                                        profileTypesRequirement &&
                                        profileTypesRequirement['properties'] &&
                                        profileTypesRequirement['properties']['profile_types'] &&
                                        Array.isArray(profileTypesRequirement['properties']['profile_types'])
                                    ) {
                                        const isSelectedProfileTypes =
                                            profileTypesRequirement['properties']['profile_types'].length > 0

                                        newError['properties']['profile_types'] = isSelectedProfileTypes
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.PROFILE_TYPE
                                              ][error]
                                    }

                                    break
                                }
                                case REQUIREMENT_TYPE_ENUM.OTHERS: {
                                    const othersRequirement = values['requirementsData'].find(
                                        (require: any) => require['type'] === REQUIREMENT_TYPE_ENUM.OTHERS,
                                    )
                                    if (!error && othersRequirement) {
                                        newError['properties']['value'] = ''
                                    }
                                    if (error && othersRequirement && othersRequirement['properties']) {
                                        newError['properties']['value'] = othersRequirement['properties']['value']
                                            ? ''
                                            : ERROR_MESSAGES[COMPLETE_SET_UP_ERRORS.REQUIREMENTS][
                                                  REQUIREMENT_TYPE_ENUM.OTHERS
                                              ][error]
                                    }

                                    break
                                }
                            }

                            if (newError && type !== 'BLOCKED_CAMPAIGNS') {
                                const returnError = { properties: newError['properties'] }
                                newRequirementErrors.push(returnError)
                            }
                        },
                    )

                    if (newRequirementErrors && newRequirementErrors.length > 0) {
                        returnError['requirementsData'] = newRequirementErrors
                    }
                    // re-mapping the schema error for requirements
                    if (
                        schemaErrors &&
                        schemaErrors['requirementsData'] &&
                        Array.isArray(schemaErrors['requirementsData'])
                    ) {
                        schemaErrors['requirementsData'].forEach((error: any, index: number) => {
                            if (
                                error['properties'] ===
                                t<string>('validation.minAndMax', {
                                    defaultValue: 'The maximum {{field}} need to be larger than the minimum {{field}}',
                                    field: 'age',
                                })
                            ) {
                                returnError['requirementsData'][index]['properties']['max'] = t<string>(
                                    'validation.minAndMax',
                                    {
                                        defaultValue:
                                            'The maximum {{field}} need to be larger than the minimum {{field}}',
                                        field: 'age',
                                    },
                                )
                                returnError['requirementsData'][index]['properties']['min'] = ''
                            } else {
                                returnError['requirementsData'][index]['properties'] = error['properties']
                            }
                        })
                    }
                }
                const isAllFieldsPassed = validateFieldsAreAllPassed(returnError)
                if (isAllFieldsPassed) {
                    return {}
                } else {
                    return returnError
                }
            } else {
                if (schemaErrors) {
                    return schemaErrors
                } else {
                    return {}
                }
            }
        },
        [formErrors, validationSchema, currentValue],
    )
    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])
    return (
        <SaveContainer>
            <Formik
                enableReinitialize={true}
                initialValues={initialValue}
                initialErrors={formErrors}
                initialTouched={initialTouched}
                validate={validate}
                onSubmit={(values: any, actions: FormikHelpers<any>): void => {
                    setValue(values)
                    const { requirementsData, payout_deadline, images, description } = values
                    let requirements = _.cloneDeep(requirementsData)
                    let blocked_campaigns: string[] | null = []

                    const occupationRequirementIdx = requirements.findIndex(
                        (requirement: Requirement) => requirement.type === REQUIREMENT_TYPE_ENUM.OCCUPATION,
                    )

                    if (occupationRequirementIdx !== -1) {
                        const occupationRequirement = requirements[occupationRequirementIdx]
                        requirements[occupationRequirementIdx].properties.occupations =
                            occupationRequirement.properties.occupations.map((occupationId: number) => ({
                                value: occupationId,
                            }))
                    }

                    const profileTypesRequirementIdx = requirements.findIndex(
                        (requirement: Requirement) => requirement.type === REQUIREMENT_TYPE_ENUM.PROFILE_TYPE,
                    )
                    if (profileTypesRequirementIdx !== -1) {
                        const profileTypeRequirement = requirements[profileTypesRequirementIdx]
                        requirements[profileTypesRequirementIdx].properties.profile_types =
                            profileTypeRequirement.properties.profile_types.map((profile_type: string) => ({
                                value: profile_type,
                            }))
                    }

                    const ageRequirement = requirements.find(
                        (requirement: Requirement) => requirement.type === REQUIREMENT_TYPE_ENUM.AGE,
                    )
                    if (ageRequirement) {
                        for (const key in ageRequirement.properties) {
                            if (ageRequirement.properties[key] === '') ageRequirement.properties[key] = null
                        }
                    }

                    const followerRequirements = requirements
                        .filter((requirement: Requirement) => requirement.type === REQUIREMENT_TYPE_ENUM.FOLLOWER)
                        .map((requirement: FollowerRequirementData) => requirement.properties.followers)
                    if (followerRequirements.length > 0) {
                        requirements = requirements
                            .filter((requirement: Requirement) => requirement.type !== REQUIREMENT_TYPE_ENUM.FOLLOWER)
                            .concat([
                                {
                                    type: REQUIREMENT_TYPE_ENUM.FOLLOWER,
                                    properties: {
                                        followers: followerRequirements,
                                    },
                                },
                            ])
                    }

                    const blockedCampaignsIdx = requirements.findIndex(
                        (requirement: Requirement) => requirement.type === BLOCKED_CAMPAIGNS_TYPE,
                    )
                    if (blockedCampaignsIdx > -1) {
                        requirements.splice(blockedCampaignsIdx, 1)
                        blocked_campaigns = blockedCampaignsUuids
                    } else {
                        blocked_campaigns = []
                    }

                    handleSubmitForm({
                        images,
                        description,
                        requirements,
                        blocked_campaigns,
                        payout_deadline: payout_deadline
                            ? convertToBackendStr(addDay(payout_deadline, 1), campaign.country)
                            : null,
                    })
                    actions.setSubmitting(false)
                    localStorage.removeItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
                }}
            >
                {(formikBag: FormikProps<any>): JSX.Element => {
                    return (
                        <CompareChangesPopup
                            initialData={formikBag.initialValues}
                            compareData={formikBag.values}
                            recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                        >
                            <Form>
                                <OnFormikValidationError
                                    callback={(): void => {
                                        showSnackBar({
                                            title: t('errorMessage.general.cantSaveChanges'),
                                            type: 'error',
                                        })
                                    }}
                                />
                                <div className="form-field">
                                    <Editor />
                                    {viewType === EDITOR_ROUTE_ENUM.EDIT && isInternalUser && (
                                        <div>
                                            <Divider className="divider" maxWidth="755px" />
                                            <CampaignEditorInputFieldContainer isEditDeadline launchFieldEditable>
                                                <Field.DatePickerField
                                                    name="payout_deadline"
                                                    isRequired
                                                    title={t('campaignEditor.completeSetup.enterPayoutDeadlineHeader')}
                                                    subtitle={t(
                                                        'campaignEditor.completeSetup.enterPayoutDeadlineSubtext',
                                                    )}
                                                    country={campaign && campaign.country}
                                                    identifier={fieldIdentifiers.payoutDeadline}
                                                />
                                            </CampaignEditorInputFieldContainer>
                                        </div>
                                    )}
                                </div>
                                <EditCampaignRestricted
                                    campaignStatus={campaign && campaign.status}
                                    isEditBrief
                                    isEditDeadline
                                    isAbleToReview={isAbleToReview}
                                    isAbleToLaunch={isAbleToLaunch}
                                >
                                    <SaveAndSubmitButtonGroup
                                        campaign={campaign}
                                        disableSave={isSaving || formikBag.isSubmitting}
                                        disableSubmit={isSaving || formikBag.isSubmitting}
                                        onSubmit={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = true
                                            formikBag.submitForm()
                                        }}
                                        onSave={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                            e.preventDefault()
                                            isPublish.current = false
                                            formikBag.submitForm()
                                        }}
                                    />
                                </EditCampaignRestricted>
                            </Form>
                        </CompareChangesPopup>
                    )
                }}
            </Formik>
        </SaveContainer>
    )
}
export default SaveForm
